import { createContext, useContext } from 'react';

// ── Portals ──────────────────────────────────────────────────────────────────
// The business runs three staff-facing portals, and every tour (`requests` doc)
// / invoice / database record belongs to exactly ONE of them:
//   'uk_to_spain'  — UK clubs/schools touring Spain (the original MST business, /staff)
//   'spain_to_uk'  — Spanish clubs/teams touring the UK (the Spanish portal, /es)
//   'padelante'    — Padelante padel tours to Alicante (the Padelante portal, /pad)
//
// A doc's portal is derived from its brand + market (see `portalOf`). A "view"
// (a portal shell, or the combined "All" roll-up) holds a LIST of portal keys it
// is allowed to show. Shared components (DashboardHome, TourCalendar, Requests,
// Invoices, …) read that list from this context and filter what they load, so
// the exact same component powers every portal without being forked.

const DEFAULT_MARKET = 'uk_to_spain';
const DEFAULT_BRAND = 'mst';

// Every portal key, in canonical order. Also the "show everything" set.
export const PORTAL_KEYS = ['uk_to_spain', 'spain_to_uk', 'padelante'];

// Display + routing metadata for each portal. `base` is the route the portal's
// own (branded/localized) shell lives at.
export const PORTAL_META = {
  uk_to_spain: { label: 'UK to Spain', base: '/staff' },
  spain_to_uk: { label: 'Spain to UK', base: '/es' },
  padelante:   { label: 'Padelante',   base: '/pad' },
};

// The /staff shell's active view ('uk_to_spain' | 'all') is held in localStorage
// so it survives section navigation. The PortalSwitcher flips it via
// setStaffView, which also fires an event so the mounted shell re-renders.
export const STAFF_VIEW_KEY = 'mst_staff_view';
export const STAFF_VIEW_EVENT = 'portal-view-change';
export function setStaffView(view) {
  try { localStorage.setItem(STAFF_VIEW_KEY, view); } catch {}
  try { window.dispatchEvent(new Event(STAFF_VIEW_EVENT)); } catch {}
}

// Where to send a user by default, given the portals they can access.
export function defaultPortalBase(portals) {
  const p = portals || [];
  if (p.includes('uk_to_spain')) return '/staff';
  if (p.includes('spain_to_uk')) return '/es';
  if (p.includes('padelante')) return '/pad';
  return '/staff';
}

const MarketContext = createContext({ portals: PORTAL_KEYS });

export function MarketProvider({ portals, children }) {
  return (
    <MarketContext.Provider value={{ portals: portals || PORTAL_KEYS }}>
      {children}
    </MarketContext.Provider>
  );
}

export function useMarket() {
  return useContext(MarketContext);
}

// The market a doc belongs to, defaulting legacy docs to uk_to_spain.
export function docMarket(d) {
  return (d && d.market) || DEFAULT_MARKET;
}

// The brand a doc belongs to, defaulting legacy docs to mst.
export function docBrand(d) {
  return (d && d.brand) || DEFAULT_BRAND;
}

// The single portal a doc belongs to. Padelante is its own portal regardless of
// market; otherwise the tour direction decides.
export function portalOf(d) {
  if (docBrand(d) === 'padelante') return 'padelante';
  if (docMarket(d) === 'spain_to_uk') return 'spain_to_uk';
  return 'uk_to_spain';
}

// True if a doc is in the given list of allowed portals. A missing list = all.
export function inPortals(d, portals) {
  if (!portals) return true;
  return portals.includes(portalOf(d));
}

// Filter an array of docs down to the allowed portals.
export function filterByPortals(docs, portals) {
  if (!portals) return docs || [];
  return (docs || []).filter((d) => portals.includes(portalOf(d)));
}

// True when a portal list covers every portal (the full roll-up).
export function isAllPortals(portals) {
  return !portals || PORTAL_KEYS.every((k) => portals.includes(k));
}

// ── Market-only helpers (still used for price-code filtering by tour direction) ─
// True if a single doc belongs in the given market view ('all' matches everything).
export function marketMatch(d, market) {
  return market === 'all' || docMarket(d) === market;
}

// Filter an array of docs down to the active market view.
export function filterByMarket(docs, market) {
  if (market === 'all') return docs;
  return (docs || []).filter((d) => docMarket(d) === market);
}

export { DEFAULT_MARKET, DEFAULT_BRAND };
